import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  Select,
  Tag,
  Popconfirm,
  message,
  AutoComplete,
  Input,
} from "antd";
import { API_BASE_URL } from "../api/api";
import CustomNumberInput from "../components/CustomNumberInput";

const { Option } = Select;

const SpecialConfigPage = () => {
  const [data, setData] = useState([]);
  const [form] = Form.useForm();
  const configType = Form.useWatch("config_type", form);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [destMap, setDestMap] = useState({});
  const [hotelMap, setHotelMap] = useState({});
  const [hotelToDestMap, setHotelToDestMap] = useState({});
  const [options, setOptions] = useState([]);

  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const [adminPwdModalOpen, setAdminPwdModalOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [pendingAction, setPendingAction] = useState(null);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}membership_special_rate_config`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await res.json();
      if (result.success && Array.isArray(result.data)) {
        const processed = result.data.map((item) => ({
          ...item,
          destination_code: Array.isArray(item.destination_code)
            ? item.destination_code.map(Number)
            : [Number(item.destination_code)],
          plan1_adult_rate: Number(item.plan1_adult_rate),
          plan1_child_rate: Number(item.plan1_child_rate),
          plan2_adult_rate: Number(item.plan2_adult_rate),
          plan2_child_rate: Number(item.plan2_child_rate),
        }));
        setData(processed);
      } else {
        message.error("Failed to load special config data.");
      }
    } catch (err) {
      console.error(err);
      message.error("An error occurred while fetching config data.");
    }
  };
  // Fetch config data from API
  useEffect(() => {
    fetchData();
  }, []);

  // Fetch destination/hotel map
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}get_dest_hotel_map`);
        const json = await res.json();
        const optionsList = [];

        json.forEach((country) => {
          optionsList.push({
            label: `${country.country} (All Cities)`,
            value: country.country,
            type: "country",
            destination_codes: country.destinations.map((d) => d.dest_code),
          });

          country.destinations.forEach((dest) => {
            optionsList.push({
              label: `${dest.dest_name}, ${country.country}`,
              value: dest.dest_name,
              type: "destination",
              code: dest.dest_code,
            });

            dest.hotels.forEach((hotel) => {
              optionsList.push({
                label: `${hotel.hotel_name}, ${dest.dest_name}`,
                value: hotel.hotel_name,
                type: "hotel",
                code: hotel.hotel_code,
                dest_code: dest.dest_code,
                dest_name: dest.dest_name,
              });
            });
          });
        });

        const uniqueOptions = Array.from(
          new Map(optionsList.map((item) => [item.label, item])).values()
        );
        setOptions(uniqueOptions);
      } catch (err) {
        console.error("Failed to fetch destination/hotel map:", err);
      }
    };

    fetchOptions();
  }, []);

  const showModal = (record = null) => {
    setEditing(record);

    if (record) {
      const destinationInput =
        record.hotel_name ||
        record.destination_name ||
        record.destination_code?.join(", ");

      const adjustedRecord = { ...record };
      if (record.config_type === "percentage") {
        adjustedRecord.plan1_adult_rate *= 100;
        adjustedRecord.plan1_child_rate *= 100;
        adjustedRecord.plan2_adult_rate *= 100;
        adjustedRecord.plan2_child_rate *= 100;
      }

      form.setFieldsValue({
        ...adjustedRecord,
        destination_input: destinationInput,
        destination_code: Array.isArray(record.destination_code)
          ? record.destination_code
          : [record.destination_code],
      });
    } else {
      form.setFieldsValue({ config_type: "percentage", is_active: 1 });
    }

    setModalOpen(true);
  };

  const handleDelete = (id) => {
    const doDelete = async (adminPwd = "") => {
      try {
        const response = await fetch(
          `${API_BASE_URL}membership_special_rate_config`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              admin_password: userInfo.id === 1 ? undefined : adminPwd,
              id: id,
            }),
          }
        );

        const result = await response.json();

        if (response.ok && result.success) {
          await fetchData();
          message.success("Record deleted");
        } else {
          throw new Error(result?.message || "Delete failed");
        }
      } catch (err) {
        console.error(err);
        message.error(err.message || "Delete failed");
      }
    };

    if (userInfo.id === 1) {
      doDelete();
    } else {
      setPendingAction(() => doDelete);
      setAdminPwdModalOpen(true);
    }
  };
  const handleSave = async () => {
    try {
      // First validate the form and print the values
      const values = await form.validateFields();

      const adjustedValues = { ...values };

      // Convert percentage values to decimals
      if (adjustedValues.config_type === "percentage") {
        adjustedValues.plan1_adult_rate /= 100;
        adjustedValues.plan1_child_rate /= 100;
        adjustedValues.plan2_adult_rate /= 100;
        adjustedValues.plan2_child_rate /= 100;
      }

      const {
        destination_code: rawDestinationCode,
        hotel_code,
        ...rest
      } = adjustedValues;
      const destination_code = rawDestinationCode.map((code) => Number(code));

      const payload = {
        ...rest,
        destination_code,
        ...(hotel_code ? { hotel_code } : {}),
        ...(editing ? { id: editing.id } : {}),
      };

      // console.log("✅ payload:", payload);

      const doSave = async (adminPwd = "") => {
        try {
          const finalPayload = {
            ...payload,
            ...(userInfo.id !== 1 ? { admin_password: adminPwd } : {}),
          };

          let response;
          let successMessage;

          if (editing) {
            response = await fetch(
              `${API_BASE_URL}membership_special_rate_config`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(finalPayload),
              }
            );
            successMessage = "Record updated";
          } else {
            response = await fetch(
              `${API_BASE_URL}membership_special_rate_config`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(finalPayload),
              }
            );
            successMessage = "Record added";
          }

          if (response.ok) {
            await fetchData();

            message.success(successMessage);
            setModalOpen(false);
            setEditing(null);
            form.resetFields();
          } else {
            const errorResult = await response.json();
            throw new Error(errorResult?.message || "API returned an error.");
          }
        } catch (err) {
          console.error(err);
          message.error(err.message || "Save failed");
        }
      };

      // Only prompt admin password *after* validation
      if (userInfo.id === 1) {
        doSave();
      } else {
        setPendingAction(() => doSave);
        setAdminPwdModalOpen(true);
      }
    } catch (err) {
      console.warn("❌ Validation failed:", err);
      // Ant Design will automatically show form field errors
    }
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "config_type",
      render: (val) => (
        <Tag color={val === "fixed" ? "volcano" : "blue"}>{val}</Tag>
      ),
    },
    { title: "Destination", dataIndex: "destination_name" },
    {
      title: "Basic Plan Adult",
      dataIndex: "plan1_adult_rate",
      render: (val, record) =>
        record.config_type === "fixed"
          ? `$${val}`
          : `${(val * 100).toFixed(2)}%`,
    },
    {
      title: "Basic Plan Child",
      dataIndex: "plan1_child_rate",
      render: (val, record) =>
        record.config_type === "fixed"
          ? `$${val}`
          : `${(val * 100).toFixed(2)}%`,
    },
    {
      title: "Gold Plan Adult",
      dataIndex: "plan2_adult_rate",
      render: (val, record) =>
        record.config_type === "fixed"
          ? `$${val}`
          : `${(val * 100).toFixed(2)}%`,
    },
    {
      title: "Gold Plan Child",
      dataIndex: "plan2_child_rate",
      render: (val, record) =>
        record.config_type === "fixed"
          ? `$${val}`
          : `${(val * 100).toFixed(2)}%`,
    },
    {
      title: "Action",
      render: (_, record) => (
        <>
          <Button
            size="small"
            onClick={() => showModal(record)}
            style={{ marginRight: 8 }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure to delete?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div>
      <h2>Special Config Page</h2>
      <Button
        type="primary"
        onClick={() => showModal()}
        style={{ marginBottom: 16 }}
      >
        Add New Record
      </Button>

      <Table
        rowKey="id"
        dataSource={data}
        columns={columns}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
        }}
      />

      <Modal
        title={editing ? "Edit Config" : "Add New Config"}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        onOk={handleSave}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="config_type"
            label="Config Type"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="fixed">Fixed</Option>
              <Option value="percentage">Percentage</Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="Destination or Hotel"
            name="destination_input"
            rules={[
              {
                required: true,
                message: "Please select a destination or hotel",
              },
            ]}
          >
            <AutoComplete
              options={options}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase())
              }
              onSelect={(value, option) => {
                if (option.type === "hotel") {
                  form.setFieldsValue({
                    destination_input: option.label,
                    destination_code: [option.dest_code], // now an array
                    hotel_code: option.code,
                  });
                } else if (option.type === "destination") {
                  form.setFieldsValue({
                    destination_input: option.label,
                    destination_code: [option.code],
                    hotel_code: null,
                  });
                } else if (option.type === "country") {
                  form.setFieldsValue({
                    destination_input: option.label,
                    destination_code: option.destination_codes, // already array
                    hotel_code: null,
                  });
                }
              }}
              placeholder="Type destination or hotel"
            />
          </Form.Item>

          <Form.Item name="destination_code" hidden />

          <Form.Item name="hotel_code" hidden />

          {[
            "plan1_adult_rate",
            "plan1_child_rate",
            "plan2_adult_rate",
            "plan2_child_rate",
          ].map((field) => {
            const isPlan1 = field.startsWith("plan1");
            const isAdult = field.includes("adult");
            const planLabel = isPlan1 ? "Basic Plan" : "Gold Plan";
            const ageLabel = isAdult ? "Adult Rate" : "Child Rate";

            return (
              <Form.Item
                key={field}
                name={field}
                label={`${planLabel} - ${ageLabel}`}
                rules={[{ required: true }]}
              >
                <CustomNumberInput
                  isFixed={configType === "fixed"}
                  isPercentage={configType === "percentage"}
                />
              </Form.Item>
            );
          })}
        </Form>
      </Modal>
      <Modal
        title="Admin Password Required"
        open={adminPwdModalOpen}
        onCancel={() => {
          setAdminPwdModalOpen(false);
          setAdminPassword("");
          setPendingAction(null);
        }}
        onOk={() => {
          setAdminPwdModalOpen(false);
          const pwd = adminPassword;
          setAdminPassword("");
          if (pendingAction) pendingAction(pwd);
        }}
        okButtonProps={{ disabled: !adminPassword }} // ✅ disables OK button when input is empty
      >
        <Input.Password
          placeholder="Enter Admin Password"
          value={adminPassword}
          onChange={(e) => setAdminPassword(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default SpecialConfigPage;
